import * as d3 from 'd3-selection'
import * as time from 'd3-time'
import Tree from 'kalpa-tree'
import layout from './lib/layout.js'
import * as util from './lib/util.js'
import axis from './lib/chart/axis.js'
import chart from './lib/chart/index.js'
import _dimensions from './lib/dimensions.js'
import minibar from './lib/mini/chart.js'
import * as scale from 'd3-scale'
import contents from 'kalpa-tree/lib/contents.js'
import brush from './lib/mini/brush.js'
import stream from 'stream-array'
import { EventEmitter } from 'events'

// 10% on each side // TODO Adjust based on screen size for #80
const extentPadding = .1

let count = 0

/*
 * Builds a new gantt chart inside of `container`
 * `data`: The tree data also containing the state of each initiative
 * `options`: kalpa tree options along with:
 *      `formatter`: used to format x-axis dates.
 *      `treeHeader`: HTML node used in the top left header
 *      `treeFooter`: HTML node used in the bottom left footer
 *      `leftPane:` Whether the left pane should be visible. Defaults to true
 *      `leftPaneWidth:` Overrides the default pane of the left pane width.
 *      `showFooter:` Whether the footer should be visible. Defaults to true
 *      `extent:` Manually set the extent used for initial load. Defaults to the extent of `initialSelection`
 */
class Gantt extends EventEmitter {

  constructor (container, data, opts) {
    super()

    var _stream = stream(data)
      , _gantt = this
      , now = new Date

    this.container = container
    this.treeData = data
    this.options = opts

    this.id = ++count

    this.options.showFooter = typeof opts.showFooter !== 'undefined' ? opts.showFooter : true
    this.options.leftPane = typeof opts.leftPane !== 'undefined' ? opts.leftPane : true
    this.width = opts.width

    this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    this.dimensions = _dimensions()

    if (!this.options.leftPane) {
      this.dimensions.left.width = 0
    }

    if (typeof this.options.leftPaneWidth !== 'undefined' && this.options.leftPane) {
      this.dimensions.left.width = this.options.leftPaneWidth
    }

    this.dimensions.right.width = this.width - this.dimensions.left.width

    // Initializes the xScaler that is used for the main chart
    this.xScale = scale.scaleTime()

    // This scale function is used for the minibar. Its domain is set to all nodes in the tree, including
    // collapsed node and fits inside of the footer dimensions
    this.miniXScale = scale.scaleTime()

    this.tree = new Tree({
      stream: _stream,
      forest: true,
      indicator: true,
      indentableSelector: '.gantt-pane-left .node-contents',
      contents: function (selection, tree, transitions) {
        selection.each(function (data) {
          var node = d3.select(this)
            , left = node.selectAll('.gantt-pane-left')
                         .data(function (d) {
                           return [d]
                         })
            , right = node.selectAll('.gantt-pane-right')
                          .data(function (d) {
                            var n = tree.get(d.id)
                            n._layout = d
                            return [n]
                          })

          left.enter()
              .append('div')
                .attr('class', 'gantt-pane-left')
                .style('width', `${_gantt.dimensions.left.width}px`) // Set the width of this to whatever the current gantt-pane-left is set to in the header in case the resizer changed the default width before this node enteredf
                .on('click', _gantt.select.bind(_gantt))

          var label = left.call(contents, tree, transitions)
              .selectAll('.label')
              .style('max-width', d => {
                // Set a max width on the label so we can style the label with an ellipse in CSS when there is a transparent
                // background. This is needed because the mask trick doesn't work when the background is transparent.
                var offset = '77px' // Room for toggler and icons
                return `calc(100% - ${d._x}px - 77px)`
              })

          right.enter()
               .append('div')
               .attr('class', 'gantt-pane-right')
               .style('margin-left', `${_gantt.dimensions.left.width}px`)
               .merge(right) // This redraws the chart since the tree data changed. TODO Maybe we can optimize this for #69
                             // It's needed since the tree `rebind` deletes the _gantt object. We need to make sure it's rebuilt
                             // w/ the new tree layout
               .call(chart, _gantt)
        })
      },
      initialSelection: this.options.initialSelection,
      accessors: {
        icon: 'type'
      },
      height: 37
    })

    // See https://github.com/SpiderStrategies/Scoreboard/issues/24354
    this.tree.on('rendered', () => this.updateWidthForScrollbars)
    this.tree.on('rebind', () => {
      // Hack. Wait for the tree to finish transitioning, then update our width if the tree forced scrollbars
      setTimeout(() => this.updateWidthForScrollbars(), this.tree.transitionTimeout)
    })

    // Once we have all the data and the tree has rendered, redraw the brush over the minibar
    _stream.on('end', () => {
      // Once the tree is loaded and the layout structure is created, we can determine the initial
      // extent. We have to wait in case a node has descendants since the extent of a node include its descendants
      this._updateMiniScale()

      if (this.options.extent) {
        // Store the extent of the minibar which determines the absolute min/max dates we'll show based on the data in
        // the tree
        let bounds = this.miniXScale.domain()
        // Use the incoming set `extent`, but pad it.
        this.extent = util.pad(this.options.extent, extentPadding)

        this.extent[0] = new Date(Math.max(bounds[0], this.extent[0]))
        this.extent[1] = new Date(Math.min(bounds[1], this.extent[1]))

        // Make sure the start of the extent isn't greater than the end of the bounds.
        // See https://github.com/SpiderStrategies/Scoreboard/issues/47285
        if (this.extent[0] > bounds[1]) {
          this.extent[0] = bounds[1]
        }
        // Similarly, make sure the end of the extent isn't below our bounds
        if (this.extent[1] < bounds[0]) {
          this.extent[1] = bounds[0]
        }

      } else {
        // Set the extent based on whatever node we're displaying
        this.extent = this._nodeExtent(data.find(d => d.id == opts.initialSelection))
      }

      this._redrawBrush()
    })
  }

  render () {
    var container = d3.select(this.container)
                      .selectAll('.gantt-container')
                      .data([this.initiativeData])
      , _container = container.enter()
                              .call(layout, this)
                              .merge(container.selectAll('.gantt-container'))

    _container.selectAll('.gantt-pane-left')
              .style('width', `${this.dimensions.left.width}px`)

    _container.selectAll('.gantt-pane-right')
              .style('margin-left', `${this.dimensions.left.width}px`)

    this.dimensions.footer.height = this._footer.node().offsetHeight

    if (container.size()) {
      this._resize()
    }

    return this
  }

  /*
   * Called when the tree has been initially rendered or updated. Used to determine if
   * there are scrollbars on the `.tree`, and if so, shrinks the space we use for the chart.
   */
  updateWidthForScrollbars () {
    // Store the current width of the right pane
    let rightWidth = this.dimensions.right.width

    // Re-run set width, which will determine if the .tree has scrollbars
    this.setWidth(this.width)

    // If the tree is in the DOM
    if (this.tree.el) {
      // Check if the tree has scrollbars by looking at the `.tree` clientWidth
      let treeWidth = this.tree.el.select('.tree').node().clientWidth - this.dimensions.left.width
      this.dimensions.right.width -= Math.abs(this.dimensions.right.width - treeWidth)
    }

    // If the width changed, that means there are scrollbars now on the tree, so we have to re-render
    if (this.dimensions.right.width != rightWidth) {
      this.render()
    }
  }

  /*
   * Sets the full width of the gantt
   */
  setWidth (width) {
    this.width = width
    this.dimensions.right.width = this.width - this.dimensions.left.width
  }

  /*
   * Sets the width of the left pane to the incoming `width`. Adjusts the right pane to fill remaining space.
   */
  setLeftPaneWidth (width) {
    var container = d3.select(this.container)

    container.selectAll('.gantt-pane-left')
             .style('width', `${width}px`)
    container.selectAll('.gantt-pane-right')
             .style('margin-left', width + 'px')

    container.select('.gantt-pane-resizer')
             .style('left', width + 'px')

    this.dimensions.left.width = width
    this.dimensions.right.width = this.width - this.dimensions.left.width

    // Re-render the gantt so all the components are drawn w/ the new width settings
    this._resize()
  }

  _resize () {
    this._redrawMini()
        ._redrawBrush(0) // No durations if we're resizing
    return this
  }

  _updateMiniScale () {
    var width = Math.max(0, this.dimensions.right.width - this.dimensions.footer.margin.left - this.dimensions.footer.margin.right)
      , extent = util.extent(this.tree.nodes, .015)

    this._extendExtent(extent)

    this.miniXScale.domain(extent) // All nodes, including collapsed
                   .range([0, width])
  }

  _redrawMini () {
    this._updateMiniScale()
    this._footer.selectAll('svg g')
                .data([this.initiativeData])
                .transition()
                .duration(this.tree.el.select('.tree').classed('transitions') ? this.tree.transitionTimeout : 0)
                .call(minibar, this.dimensions, this.miniXScale, this.today)
    return this
  }

  _redrawMain () {
    // Updates the scaler domain/range
    this.xScale.domain(this.extent)
               .range([0, this.dimensions.right.width])

    // Update the axis
    this._header.selectAll('.gantt-pane-right')
                .data([this.initiativeData])
                .call(axis, this.xScale, this.options.formatter, this.today)

    // Update the main chart
    this._body.selectAll('.node')
              .selectAll('.gantt-pane-right')
              .call(chart, this)

    return this
  }

  /*
   * Sets the date range extent
   */
  setExtent (extent) {
    if (extent) {
      const padded = util.pad(extent, extentPadding)

       // Make sure the incoming extent doesn't exceed the actual data we can display. See #115
      this.extent = this._constrainExtent(padded)
      this._extendExtent(this.extent)
    } else {
      // Clearing, set extent to the default node that was loaded
      this.extent = this._nodeExtent(this.treeData.find(d => d.id == this.options.initialSelection))
    }
    this._redrawBrush()
  }

  toggleLeftPane (visible) {
    if (visible) {
      this.setLeftPaneWidth(this.dimensions.left._width || this.dimensions.left.defaultWidth)
    } else {
      // Keep track of the current width in case it's toggled on
      this.dimensions.left._width = this.dimensions.left.width
      this.setLeftPaneWidth(0)
    }
    this._resize()
  }

  toggleFooter (visible) {
    this.options.showFooter = visible
    d3.select(this.container)
      .selectAll('.gantt-container')
      .classed('gantt-footerless', !visible)

    // Update the footer height
    this.dimensions.footer.height = this._footer.node().offsetHeight
    this._resize()
  }

  /*
   * When the brush is adjusted, we need to check if the current-day indicator in the minibar
   * is covered by the brush extent (i.e. dates we're showing) so we can darker the style so it's easier to see the line.
   * Ideally we'd just recall the minibar code, but for performance reasons it's cleaner to just update the current line class here.
   */
  _miniChartOverlay () {
    this._footer.selectAll('svg g g.gantt-mini-items .current-day')
                .classed('current-day-in-extent', this.today >= this.extent[0] && this.today <= this.extent[1])
  }

  _redrawBrush (animationDuration) {
    this._updateMiniScale()
    this._footer.selectAll('svg > g')
                .data([this.initiativeData])
                .on('brush', () => {
                  this._redrawMain()
                  this._miniChartOverlay()
                })
                .call(brush, this.dimensions, this.extent, this.miniXScale, animationDuration)

    return this
  }

  /*
   * Extends the extent by a day in both directions if the extent is a single day (e.g. milestones)
   */
  _extendExtent (extent) {
    if (extent[0].getTime() === extent[1].getTime()) {
      // Trying to show a single date. Extend by one day in both directions
      var start = new Date(extent[0])
        , end = new Date(extent[1])
        , extension = 1 // days

      start.setDate(start.getDate() - extension)
      end.setDate(end.getDate() + extension)
      extent[0] = start
      extent[1] = end
    }
  }

  /*
   * Constrains the incoming `extent` to the range of defined by the minibar's
   * domain. We can't set the extent so it's outside the miniXScale domain,
   * otherwise the brush will be outside of the minichart. This could result
   * in a node that isn't truly centered, but that matches the milestone logic,
   * so we should be okay
   */
  _constrainExtent (extent) {
    let miniDomain = this.miniXScale.domain()

    // Ensure the extent is within the miniDomain
    let start = new Date(Math.max(extent[0], miniDomain[0]))
    let end = new Date(Math.min(extent[1], miniDomain[1]))

    // Ensure the start date is less than or equal to the end date
    if (start > end) {
      [start, end] = [end, start]
    }

    return [start, end]
  }

  /*
   * Returns the extent for a node.
   */
  _nodeExtent (node) {
    let extent = []

    if (node.type === 'milestone' && node.endDate) {
      // Don't zoom on the milestone, just pan so it's centered
      var full = util.extent(this.tree.nodes) // full extent based on all nodes
        , showing = this.extent ? time.timeDay.count(this.extent[0], this.extent[1]) : 0 // How many days we're currently showing
        , mid = showing / 2 // midpoint
        , startOffset = time.timeDay.offset(node.endDate, -mid)
        , endOffset = time.timeDay.offset(node.endDate, mid)

      if (this.extent && (node.endDate.getTime() < this.extent[0].getTime() || node.endDate.getTime() > this.extent[1].getTime())) {
        // The milestone isn't currently in view, force it as close to the center as we can
        extent[0] = new Date(Math.max(full[0], startOffset))
        extent[1] = new Date(Math.min(full[1], endOffset))
      } else if (startOffset.getTime() > full[0].getTime() && endOffset.getTime() < full[1].getTime()) {
        // Center the milestone
        extent[0] = startOffset
        extent[1] = endOffset
      } else if (this.extent) {
        // Default to whatever it was, we can't move this milestone
        extent = this.extent
      } else {
        // Use the full range as the extent, this milestone is probably right on the edge, preventing blowups
        extent[0] = full[0]
        extent[1] = full[1]
      }

      extent = this._constrainExtent(extent)
    } else if (!node._layout.children && node._layout.parent && node.endDate && node.startDate) {
      // See scoreboard #27715. This node doesn't have children and has a parent

      // The node should take up 1/3 of the available space. If we pad by 100%, this gives us 1/3 on left, 1/3 on right, and 1/3 of available
      // space for the actual drawing
      extent = util.pad([node.startDate, node.endDate], 1)

      // Grab the parents extent
      let parentExtent = this._constrainExtent(util.extent(this.tree._descendants(this.tree._layout[node._layout.parent.id]).map(d => this.tree.get(d.id)), extentPadding))
      if (extent[0] < parentExtent[0]) {
        // The node starts before the range of the parent
        let days = time.timeDay.count(extent[0], parentExtent[0])
        extent[0] = parentExtent[0]
        extent[1] = new Date(Math.min(parentExtent[1], time.timeDay.offset(extent[1], days)))
      }
      if (extent[1] > parentExtent[1]) {
        let days = time.timeDay.count(extent[1], parentExtent[1])
        extent[1] = parentExtent[1]
        extent[0] = new Date(Math.max(parentExtent[0], time.timeDay.offset(extent[0], days)))
      }
    } else if (node.endDate && node.startDate) {
      // Task or initiative w/ children, update the extent
      extent = util.extent(this.tree._descendants(this.tree._layout[node.id]).map(d => this.tree.get(d.id)), extentPadding)
      extent = this._constrainExtent(extent)
    } else {
      // We can't determine the extent from this node, so default to all nodes
      extent = util.extent(this.tree.nodes, extentPadding)
      extent = this._constrainExtent(extent)
    }

    this._extendExtent(extent)

    return extent
  }

  /*
   * Fired when a `gantt-pane-left` element within the tree is clicked.
   * The chart should be redrawn so the element clicked is zoomed or panned
   */
  select (e, d) {
    var node = this.tree.get(d.id)
      , extent = this._nodeExtent(node)

    this.extent[0] = extent[0]
    this.extent[1] = extent[1]

    // We just have to redraw the brush if they selected the node. The brush will
    // update the main chart when it updates the selection
    this._redrawBrush()
  }

}

export default Gantt
